import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  signal
} from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { CommunityService } from '../../services/community.service';
import {
  CommunityReactionResponse,
  DEFAULT_REACTION_KEYS,
  communityErrorKey,
  reactionEmoji
} from '../../models/community.models';

/**
 * Compact reaction bar (F7): one emoji button per key of the channel's
 * `reaction_set`, with the count, highlighted for the viewer's own reactions.
 *
 * Self-contained: it calls the toggle endpoint itself and keeps its own counts,
 * so it can sit inside the recursive post component without plumbing events up
 * through every level. Clicks update optimistically and settle on the server's
 * answer; on an error the previous state is restored and `failed` carries the
 * translation key for the host to show.
 */
@Component({
  selector: 'app-community-reactions',
  standalone: true,
  imports: [CommonModule, TranslocoModule],
  templateUrl: './community-reactions.component.html',
  styleUrl: './community-reactions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunityReactionsComponent implements OnChanges {
  @Input({ required: true }) target!: 'thread' | 'post';
  @Input({ required: true }) targetId!: number;
  /** The channel's reaction set; falls back to the default five. */
  @Input() reactionSet: string[] | null | undefined;
  @Input() reactions: Record<string, number> | null | undefined;
  @Input() myReactions: string[] | null | undefined;
  @Input() canReact = false;

  @Output() failed = new EventEmitter<string>();

  counts = signal<Record<string, number>>({});
  mine = signal<Set<string>>(new Set());
  busy = signal<string | null>(null);

  readonly emoji = reactionEmoji;

  get keys(): string[] {
    const set = this.reactionSet && this.reactionSet.length ? this.reactionSet : [...DEFAULT_REACTION_KEYS];
    // A count on a key that left the set stays visible (read-only) instead of vanishing.
    const extra = Object.keys(this.counts()).filter(k => !set.includes(k) && this.counts()[k] > 0);
    return [...set, ...extra];
  }

  constructor(private communityService: CommunityService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reactions'] || changes['targetId']) this.counts.set({ ...(this.reactions ?? {}) });
    if (changes['myReactions'] || changes['targetId']) this.mine.set(new Set(this.myReactions ?? []));
  }

  count(key: string): number {
    return this.counts()[key] ?? 0;
  }

  inSet(key: string): boolean {
    const set = this.reactionSet && this.reactionSet.length ? this.reactionSet : DEFAULT_REACTION_KEYS;
    return set.includes(key);
  }

  toggle(key: string): void {
    if (!this.canReact || this.busy() || !this.inSet(key)) return;

    const prevCounts = this.counts();
    const prevMine = this.mine();
    const had = prevMine.has(key);
    const nextMine = new Set(prevMine);
    had ? nextMine.delete(key) : nextMine.add(key);
    this.mine.set(nextMine);
    this.counts.set({ ...prevCounts, [key]: Math.max(0, (prevCounts[key] ?? 0) + (had ? -1 : 1)) });
    this.busy.set(key);

    const request = this.target === 'thread'
      ? this.communityService.reactToThread(this.targetId, key)
      : this.communityService.reactToPost(this.targetId, key);

    request.subscribe({
      next: (res: CommunityReactionResponse) => {
        this.counts.set({ ...(res.reactions ?? {}) });
        this.mine.set(new Set(res.my_reactions ?? []));
        this.busy.set(null);
      },
      error: err => {
        this.counts.set(prevCounts);
        this.mine.set(prevMine);
        this.busy.set(null);
        this.failed.emit(communityErrorKey(err));
      }
    });
  }
}
